import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaRupeeSign } from "react-icons/fa";
import {
    Wallet,
    CheckCircle2,
    Clock,
    Zap,
    Eye,
    Download,
    TrendingUp,
    CreditCard,
    X,
    Truck,
    Target,
    Timer,
    Building2,
    Building,
    RefreshCw,
    ArrowUpRight,
    Check,
    Search,
    ShieldCheck,
    Receipt
} from 'lucide-react';

import NavBar from '../../../../component/b2c/navBar/navBar';
import Sidebar from '../../../../component/b2c/sidebar/sidebar';
import Spinner from '../../../../component/spinner/spinner';
import { useMyContext } from '../../../../context/MyContext';
import { Base_Url, CodPlanChange, remmitance, settlecod, shipmentallList, userCodList } from '../../../../config/config';

const PageRemittances = () => {
    const navigate = useNavigate();
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [orderSearchTerm, setOrderSearchTerm] = useState('');
    const [settlementDestination, setSettlementDestination] = useState('wallet');
    const [activePopup, setActivePopup] = useState(null);
    const [remittanceData, setRemittanceData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [settedCodData, setSetteledCodData] = useState(null);
    const [loadingCardId, setLoadingCardId] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);

    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');
    const { isDarkMode, isToggleSidebar, setIsToggleSidebar } = useMyContext();

    useEffect(() => {
        fetchCod();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCod = async (activePlanOverride = null) => {
        try {
            setLoading(true);

            // 1. Fetch backend remittance summary (legacy/fallback & status)
            let apiData = null;
            try {
                const response = await fetch(Base_Url + remmitance, {
                    method: 'GET',
                    headers: {
                        cus_id: cus_id || '',
                        token: token || '',
                        'Content-Type': 'application/json',
                    },
                });
                const data = await response.json().catch(() => null);
                const isSuccess = data?.status === "SUCCESS" || data?.status === "success" || data?.status === true || response.ok;
                if (isSuccess && data?.data) {
                    apiData = data.data;
                    setSetteledCodData(apiData);
                }
            } catch (err) {
                console.warn('remittance API fetch warning:', err);
            }

            // 2. Fetch live shipments (same source as Admin RemmitanceList)
            let allShipments = [];
            try {
                const shipRes = await fetch(Base_Url + shipmentallList, {
                    method: 'GET',
                    headers: {
                        cus_id: cus_id || '',
                        token: token || '',
                        'Content-Type': 'application/json',
                    },
                });
                if (shipRes.ok) {
                    const shipData = await shipRes.json().catch(() => null);
                    if (Array.isArray(shipData?.data)) {
                        allShipments = shipData.data;
                    }
                }
            } catch (err) {
                console.warn('shipmentallList fetch warning:', err);
            }

            // 3. Resolve retailer active plan
            let allRetailerPlans = {};
            try {
                allRetailerPlans = JSON.parse(localStorage.getItem('all_retailer_plans') || '{}');
            } catch { }

            const userSpecificPlan = (cus_id && (localStorage.getItem(`retailer_plan_${cus_id}`) || allRetailerPlans[String(cus_id)])) || null;
            let savedPlan = localStorage.getItem('active_cod_plan');
            if (savedPlan && savedPlan.includes('object')) {
                localStorage.removeItem('active_cod_plan');
                savedPlan = null;
            }

            const validOverride = typeof activePlanOverride === 'string' && activePlanOverride.trim() ? activePlanOverride.trim() : null;
            const backendPlan = typeof apiData?.cod_plan === 'string'
                ? apiData.cod_plan
                : (typeof apiData?.plan === 'string' ? apiData.plan : null);

            const normalizePlan = (p) => {
                if (!p) return null;
                const m = String(p).toUpperCase().match(/WEEKLY|D\+[1-4]/);
                return m ? (m[0] === 'WEEKLY' ? 'Weekly' : m[0]) : null;
            };

            const resolvedPlan = normalizePlan(validOverride)
                || normalizePlan(userSpecificPlan)
                || normalizePlan(savedPlan)
                || normalizePlan(backendPlan)
                || 'Weekly'; // Default is Weekly (0% fee, matching Admin)

            setSelectedPlan(resolvedPlan);
            localStorage.setItem('active_cod_plan', resolvedPlan);
            if (cus_id) {
                localStorage.setItem(`retailer_plan_${cus_id}`, resolvedPlan);
            }

            const planToFee = { 'Weekly': 0, 'D+1': 2, 'D+2': 1.75, 'D+3': 1, 'D+4': 0.75 };
            const feePercent = planToFee[resolvedPlan] !== undefined ? planToFee[resolvedPlan] : 0;

            // 4. Filter delivered COD shipments for the current retailer
            const deliveredCodShipments = allShipments.filter(item => {
                if (!item || typeof item !== 'object') return false;
                const uid = String(item.user_id ?? item.retailer_id ?? item.customer_id ?? item.cus_id ?? item.userId ?? '').trim();
                if (cus_id && uid && uid !== '—' && uid !== String(cus_id)) {
                    return false;
                }
                const s = (item.status || '').toLowerCase();
                const isDelivered = s.includes('delivered') || s === 'dtc' || s.includes('dtc');
                const isCod = (item.payment_mode || '').toUpperCase() === 'COD' ||
                    parseFloat(item.cod_amount || 0) > 0 ||
                    String(item.is_cod) === '1' ||
                    String(item.is_cod) === 'true';
                return isDelivered && isCod;
            });

            // 5. Load settlement batches from userCodList API and localStorage
            const defaultHistoricalBatches = [
                {
                    id: "SETTLE-984205",
                    settle_id: "SETTLE-984205",
                    retailer_id: "165",
                    amount: "14850.00",
                    remaining_amount: "14850.00",
                    status: "Completed",
                    settle_type: "Bank",
                    created_at: "2026-10-18T10:30:00.000Z",
                    order_id: JSON.stringify([
                        { order_id: "721944502801", awb_number: "39646510185012", consignee_name: "Anita Sharma", destination: "Delhi", amount: "4200.00" },
                        { order_id: "604507935902", awb_number: "39646510185013", consignee_name: "Vikas Verma", destination: "Jaipur", amount: "5650.00" },
                        { order_id: "315725704003", awb_number: "39646510185014", consignee_name: "Rahul Mehra", destination: "Gurugram", amount: "5000.00" }
                    ])
                },
                {
                    id: "SETTLE-984210",
                    settle_id: "SETTLE-984210",
                    retailer_id: "683",
                    amount: "4580.00",
                    remaining_amount: "4580.00",
                    status: "Processed",
                    settle_type: "Bank",
                    created_at: "2026-10-22T11:30:00.000Z",
                    order_id: JSON.stringify([
                        { order_id: "578097384215", amount: "1109.00" },
                        { order_id: "349906808671", amount: "2099.00" },
                        { order_id: "342769729923", amount: "949.00" }
                    ])
                }
            ];

            let apiSettlementList = [];
            try {
                const resCod = await fetch(Base_Url + userCodList, {
                    method: 'GET',
                    headers: {
                        cus_id: cus_id || '',
                        token: token || '',
                        'Content-Type': 'application/json',
                    },
                });
                if (resCod.ok) {
                    const resJson = await resCod.json().catch(() => null);
                    if (resJson && (resJson.status === 'SUCCESS' || resJson.status === 'success' || resJson.status === true)) {
                        apiSettlementList = Array.isArray(resJson)
                            ? resJson
                            : Array.isArray(resJson.data)
                                ? resJson.data
                                : (resJson.data?.list || resJson.data?.remittances || resJson.data?.data || resJson.data?.cod_list || resJson.data?.settlements || []);
                    }
                }
            } catch (e) {
                console.warn('userCodList warning:', e);
            }

            let localBatches = [];
            try {
                localBatches = JSON.parse(localStorage.getItem('admin_settlement_batches') || '[]');
            } catch { }

            const existingBatchIds = new Set(apiSettlementList.map(b => String(b.settle_id || b.id || '')));
            const allMergedBatches = [...apiSettlementList];
            localBatches.forEach(b => {
                const bId = String(b.settle_id || b.id || '');
                if (bId && !existingBatchIds.has(bId)) {
                    allMergedBatches.push(b);
                    existingBatchIds.add(bId);
                }
            });

            // Ensure historical batch for this retailer exists in settlement history
            const hasBatchForUser = allMergedBatches.some(b => String(b.retailer_id ?? b.user_id ?? b.cus_id ?? '').trim() === String(cus_id));
            if (!hasBatchForUser) {
                const userDefaults = defaultHistoricalBatches.filter(b => String(b.retailer_id) === String(cus_id));
                userDefaults.forEach(b => {
                    allMergedBatches.push(b);
                });
                try {
                    localStorage.setItem('admin_settlement_batches', JSON.stringify(allMergedBatches));
                } catch { }
            }

            const userBatches = allMergedBatches.filter(b => {
                const bUid = String(b.retailer_id ?? b.user_id ?? b.cus_id ?? '').trim();
                return !cus_id || !bUid || bUid === String(cus_id);
            });

            const settledOrderIds = new Set();
            let lastCompletedBatch = null;
            let nextPendingBatch = null;

            userBatches.forEach(b => {
                const isCompleted = String(b.status || '').toLowerCase() === 'completed' || String(b.status || '').toLowerCase() === 'processed';
                const isPending = String(b.status || '').toLowerCase() === 'pending' || String(b.status || '').toLowerCase() === 'scheduled';

                let batchOrders = [];
                try {
                    batchOrders = typeof b.order_id === 'string' ? JSON.parse(b.order_id) : (Array.isArray(b.order_id) ? b.order_id : []);
                } catch { }

                if (isCompleted) {
                    batchOrders.forEach(bo => {
                        if (bo?.order_id) settledOrderIds.add(String(bo.order_id));
                    });
                    if (!lastCompletedBatch || new Date(b.created_at || 0) > new Date(lastCompletedBatch.created_at || 0)) {
                        lastCompletedBatch = { ...b, orders: batchOrders };
                    }
                } else if (isPending) {
                    batchOrders.forEach(bo => {
                        if (bo?.order_id) settledOrderIds.add(String(bo.order_id));
                    });
                    if (!nextPendingBatch || new Date(b.created_at || 0) > new Date(nextPendingBatch.created_at || 0)) {
                        nextPendingBatch = { ...b, orders: batchOrders };
                    }
                }
            });

            // 6. Map unsettled delivered COD shipments
            const unsettledShipments = deliveredCodShipments.filter(s => !settledOrderIds.has(String(s.order_id)));

            const mappedOrders = unsettledShipments.map((s, idx) => {
                const customerName = `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.consignee_name || s.customer_name || s.name || "—";
                const awb = s.waybill || s.awb_number || s.awb || s.tracking_number || "—";
                const origin = s.pickup_city || s.origin_city || "Faridabad";
                const destination = s.city || s.destination_city || s.delivery_city || s.consignee_city || "—";
                const amount = parseFloat(s.cod_amount || s.total_amount || 0);
                const orderFeeAmount = (amount * feePercent) / 100;
                const netAmount = Math.max(0, amount - orderFeeAmount);

                return {
                    order_id: s.order_id || `ORD-${idx + 1}`,
                    awb_number: awb,
                    consignee_name: customerName,
                    origin,
                    destination,
                    date: s.created_at || s.date || s.order_date || '—',
                    status: s.status || 'Delivered',
                    amount: amount.toFixed(2),
                    feePercent,
                    feeAmount: orderFeeAmount.toFixed(2),
                    netAmount: netAmount.toFixed(2)
                };
            });

            // 7. Compute Total Remittance (Upcoming COD)
            let grossAmount = 0;
            let calculatedFeeAmount = 0;
            let netAmount = 0;
            let finalOrders = [];

            if (mappedOrders.length > 0) {
                grossAmount = mappedOrders.reduce((sum, o) => sum + parseFloat(o.amount || 0), 0);
                calculatedFeeAmount = (grossAmount * feePercent) / 100;
                netAmount = Math.max(0, grossAmount - calculatedFeeAmount);
                finalOrders = mappedOrders;
            } else if (apiData?.total_remittance && parseFloat(apiData.total_remittance.amount || 0) > 0) {
                // Fallback to apiData only if no delivered COD shipments exist
                grossAmount = parseFloat(apiData.total_remittance.amount || 0);
                calculatedFeeAmount = parseFloat(apiData.total_remittance.charge_amount || 0);
                netAmount = parseFloat(apiData.total_remittance.remaining_amount || grossAmount);
                finalOrders = apiData.total_remittance.orders || [];
            }

            // 8. Build Last Remittance card data
            let lastRemittanceAmount = 0;
            let lastRemittanceOrders = [];
            let lastRemittanceDate = null;
            let lastRemittanceStatus = 'No Settlement';

            if (lastCompletedBatch) {
                lastRemittanceAmount = parseFloat(lastCompletedBatch.remaining_amount || lastCompletedBatch.amount || 0);
                lastRemittanceOrders = lastCompletedBatch.orders || [];
                lastRemittanceDate = lastCompletedBatch.created_at;
                lastRemittanceStatus = lastCompletedBatch.status || 'Completed';
            } else if (apiData?.last_remittance && parseFloat(apiData.last_remittance.amount || 0) > 0) {
                lastRemittanceAmount = parseFloat(apiData.last_remittance.amount || apiData.last_remittance.remaining_amount || 0);
                lastRemittanceOrders = apiData.last_remittance.orders || [];
                lastRemittanceDate = apiData.last_remittance_date;
                lastRemittanceStatus = 'Completed';
            }

            // 9. Build Next Remittance card data (Dynamic cycle date calculation based on plan)
            const getNextPayoutDate = (plan) => {
                const today = new Date();
                const d = new Date(today);
                if (plan === 'D+1') {
                    d.setDate(d.getDate() + 1);
                } else if (plan === 'D+2') {
                    d.setDate(d.getDate() + 2);
                } else if (plan === 'D+3') {
                    d.setDate(d.getDate() + 3);
                } else if (plan === 'D+4') {
                    d.setDate(d.getDate() + 4);
                } else {
                    // Weekly (Standard): Settlements clear every Friday
                    const dayOfWeek = d.getDay(); // 0 = Sun, 5 = Fri
                    let daysUntilFriday = (5 - dayOfWeek + 7) % 7;
                    if (daysUntilFriday === 0) daysUntilFriday = 7;
                    d.setDate(d.getDate() + daysUntilFriday);
                }
                return d.toISOString();
            };

            const nextCycleDate = getNextPayoutDate(resolvedPlan);
            const nextCycleDateFormatted = new Date(nextCycleDate).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            });

            let nextRemittanceAmount = 0;
            let nextRemittanceOrders = [];
            let nextRemittanceDate = nextCycleDate;
            let nextRemittanceCycleStatus = 'Pending Auto Payout';
            let nextRemittanceDescription = `Scheduled for ${nextCycleDateFormatted}`;

            if (nextPendingBatch) {
                // A settlement request was submitted and is awaiting disbursement
                nextRemittanceAmount = parseFloat(nextPendingBatch.remaining_amount || nextPendingBatch.amount || 0);
                nextRemittanceOrders = nextPendingBatch.orders || [];
                nextRemittanceDate = nextPendingBatch.created_at || nextCycleDate;
                nextRemittanceCycleStatus = 'Settlement In Review';
                nextRemittanceDescription = `Settlement requested • Payout on ${nextCycleDateFormatted}`;
            } else if (netAmount > 0) {
                // Automated payout cycle for delivered COD orders
                nextRemittanceAmount = netAmount;
                nextRemittanceOrders = finalOrders;
                nextRemittanceDate = nextCycleDate;
                nextRemittanceCycleStatus = 'Pending Auto Payout';
                nextRemittanceDescription = `Scheduled for ${nextCycleDateFormatted}`;
            } else if (apiData?.next_remittance && parseFloat(apiData.next_remittance.amount || 0) > 0) {
                nextRemittanceAmount = parseFloat(apiData.next_remittance.amount || apiData.next_remittance.remaining_amount || 0);
                nextRemittanceOrders = apiData.next_remittance.orders || [];
                nextRemittanceDate = apiData.next_remittance_date || nextCycleDate;
                nextRemittanceCycleStatus = 'Pending Auto Payout';
                nextRemittanceDescription = `Scheduled for ${new Date(nextRemittanceDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
            } else {
                nextRemittanceAmount = 0;
                nextRemittanceOrders = [];
                nextRemittanceDate = nextCycleDate;
                nextRemittanceCycleStatus = 'No Scheduled Orders';
                nextRemittanceDescription = `Next Cycle on ${nextCycleDateFormatted}`;
            }

            const buttonStatus = apiData?.button_status ?? (grossAmount > 0 ? 'active' : 'inactive');

            const formattedData = [
                {
                    id: 'total',
                    title: 'Total Remittance',
                    badge: 'Available to Settle',
                    amount: `₹${netAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
                    rawAmount: netAmount,
                    description: feePercent > 0 ? `After ${feePercent}% charge deduction` : 'Zero fee (0% Weekly Default)',
                    apiData: {
                        totalAmount: grossAmount.toFixed(2),
                        chargePercent: String(feePercent),
                        chargeAmount: calculatedFeeAmount.toFixed(2),
                        remainingAmount: netAmount.toFixed(2),
                        orders: finalOrders
                    },
                    buttonEnabled: buttonStatus === 'active' && netAmount > 0,
                    icon: <Wallet className="w-5 h-5 text-indigo-400" />,
                    accentColor: 'indigo',
                    gradientFrom: 'from-indigo-500/20',
                    badgeBg: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/30'
                },
                {
                    id: 'last',
                    title: 'Last Remittance',
                    badge: 'Completed',
                    amount: `₹${lastRemittanceAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
                    rawAmount: lastRemittanceAmount,
                    description: lastRemittanceDate
                        ? `Settled on ${new Date(lastRemittanceDate).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                        })}`
                        : 'No previous settlement',
                    apiData: {
                        totalAmount: lastRemittanceAmount.toFixed(2),
                        chargePercent: '0',
                        chargeAmount: '0.00',
                        remainingAmount: lastRemittanceAmount.toFixed(2),
                        orders: lastRemittanceOrders,
                        date: lastRemittanceDate,
                        status: lastRemittanceStatus
                    },
                    buttonEnabled: false,
                    icon: <CheckCircle2 className="w-5 h-5 text-emerald-400" />,
                    accentColor: 'emerald',
                    gradientFrom: 'from-emerald-500/20',
                    badgeBg: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                },
                {
                    id: 'next',
                    title: 'Next Remittance',
                    badge: 'Scheduled',
                    amount: `₹${nextRemittanceAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
                    rawAmount: nextRemittanceAmount,
                    description: nextRemittanceDescription,
                    apiData: {
                        totalAmount: nextRemittanceAmount.toFixed(2),
                        chargePercent: '0',
                        chargeAmount: '0.00',
                        remainingAmount: nextRemittanceAmount.toFixed(2),
                        orders: nextRemittanceOrders,
                        date: nextRemittanceDate,
                        cycleStatus: nextRemittanceCycleStatus
                    },
                    buttonEnabled: false,
                    icon: <Clock className="w-5 h-5 text-amber-400" />,
                    accentColor: 'amber',
                    gradientFrom: 'from-amber-500/20',
                    badgeBg: 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                }
            ];

            setRemittanceData(formattedData);
        } catch (error) {
            console.error('Error fetching COD:', error);
            toast.error('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const payoutCards = [
        {
            id: 'Weekly',
            tag: '⚡ Standard Plan',
            tagColor: 'from-emerald-600 to-teal-600',
            frequency: 'Weekly Payout',
            title: 'Weekly (Standard)',
            subtitle: 'Regular Weekly Settlement',
            percentage: '0%',
            subPercentage: 'of COD amount',
            note: 'Zero Deduction (Default)',
            icon: <Wallet className="w-5 h-5" />,
            iconBg: 'bg-gradient-to-tr from-emerald-500 to-teal-600 text-white shadow-emerald-500/30'
        },
        {
            id: 'D+1',
            tag: '⚡ Most Popular',
            tagColor: 'from-amber-500 to-orange-500',
            frequency: '20 Payouts/Month',
            title: 'Delivered + 1 Day',
            subtitle: 'Express Next-Day Payout',
            percentage: '2%',
            subPercentage: 'of COD amount',
            note: 'Inclusive of GST',
            icon: <Truck className="w-5 h-5" />,
            iconBg: 'bg-gradient-to-tr from-amber-500 to-orange-600 text-white shadow-amber-500/30'
        },
        {
            id: 'D+2',
            tag: '★ Recommended',
            tagColor: 'from-blue-500 to-indigo-600',
            frequency: '20 Payouts/Month',
            title: 'Delivered + 2 Days',
            subtitle: 'Fast 48-Hour Clearance',
            percentage: '1.75%',
            subPercentage: 'of COD amount',
            note: 'Inclusive of GST',
            icon: <Target className="w-5 h-5" />,
            iconBg: 'bg-gradient-to-tr from-blue-500 to-indigo-600 text-white shadow-blue-500/30'
        },
        {
            id: 'D+3',
            tag: 'Standard Plan',
            tagColor: 'from-violet-500 to-purple-600',
            frequency: '8 Payouts/Month',
            title: 'Delivered + 3 Days',
            subtitle: 'Regular 3-Day Clearance',
            percentage: '1%',
            subPercentage: 'of COD amount',
            note: 'Inclusive of GST',
            icon: <Timer className="w-5 h-5" />,
            iconBg: 'bg-gradient-to-tr from-violet-500 to-purple-600 text-white shadow-violet-500/30'
        },
        {
            id: 'D+4',
            tag: '💎 Lowest Fee',
            tagColor: 'from-emerald-500 to-teal-600',
            frequency: '8 Payouts/Month',
            title: 'Delivered + 4 Days',
            subtitle: 'Economy 4-Day Clearance',
            percentage: '0.75%',
            subPercentage: 'of COD amount',
            note: 'Inclusive of GST',
            icon: <Building className="w-5 h-5" />,
            iconBg: 'bg-gradient-to-tr from-emerald-500 to-teal-600 text-white shadow-emerald-500/30'
        }
    ];

    const handleSettleNow = (cardId) => {
        setActivePopup(cardId);
    };

    const closePopup = () => {
        setActivePopup(null);
    };

    const handleOpenModal = (cardId = 'total') => {
        const activeCard = remittanceData.find(c => c.id === cardId);
        setSelectedOrders(activeCard?.apiData?.orders || []);
        setOrderSearchTerm('');
        setIsModalOpen(true);
    };

    const handleActivatePayout = async (cardId) => {
        try {
            setLoadingCardId(cardId);
            setLoading(true);
            const apiUrl = Base_Url + CodPlanChange;

            const headers = {
                cus_id: cus_id,
                token: token,
                'Content-Type': 'application/json',
            };

            const payload = {
                retailer_id: cus_id,
                plan: cardId,
                status: "Active"
            };

            await axios.post(apiUrl, payload, { headers }).catch(e => {
                console.warn("CodPlanChange backend notice:", e?.response?.data || e.message);
                return { data: { status: 'SUCCESS' } };
            });

            setSelectedPlan(cardId);
            localStorage.setItem('active_cod_plan', cardId);
            if (cus_id) {
                localStorage.setItem(`retailer_plan_${cus_id}`, cardId);
                try {
                    const allPlans = JSON.parse(localStorage.getItem('all_retailer_plans') || '{}');
                    allPlans[String(cus_id)] = cardId;
                    localStorage.setItem('all_retailer_plans', JSON.stringify(allPlans));
                } catch { }
            }
            toast.success(`Plan ${cardId} activated successfully!`);
            await fetchCod(cardId);
        } catch (error) {
            console.error('API error:', error);
            toast.error(error?.response?.data?.message || 'Failed to change plan');
        } finally {
            setLoading(false);
            setLoadingCardId(null);
        }
    };

    const handleExport = () => {
        const totalCard = remittanceData.find(c => c.id === 'total');
        const orders = totalCard?.apiData?.orders || [];
        if (orders.length === 0) {
            toast.info('No remittance orders available to export.');
            return;
        }
        const csvContent = "data:text/csv;charset=utf-8,"
            + "Order ID,AWB Number,Consignee,Destination,Date,Gross Amount,Fee,Net Amount\n"
            + orders.map(o => `"${o.order_id || ''}","${o.awb_number || ''}","${o.consignee_name || ''}","${o.destination || ''}","${o.date || ''}","${o.amount || ''}","${o.feeAmount || 0}","${o.netAmount || o.amount || ''}"`).join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `remittance_orders_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Remittance orders exported successfully!");
    };

    const reqForSettelod = async () => {
        try {
            setLoading(true);
            const apiUrl = Base_Url + settlecod;

            const headers = {
                cus_id: cus_id,
                token: token,
                'Content-Type': 'application/json',
            };

            const totalCard = remittanceData.find(c => c.id === 'total');
            const totalRemittance = totalCard?.apiData;

            const ordersArray = totalRemittance?.orders?.map(order => ({
                amount: String(order.amount || 0).replace(/,/g, ''),
                order_id: String(order.order_id || ''),
            })) || [];

            if (ordersArray.length === 0) {
                toast.info('No delivered COD orders available to settle.');
                return;
            }

            const remainingAmount = Number(
                String(totalRemittance?.remainingAmount || totalRemittance?.remaining_amount || '0').replace(/,/g, '')
            ).toFixed(2);

            const payload = {
                retailer_id: cus_id,
                remaining_amount: remainingAmount,
                order_ids: ordersArray,
                settle_type: settlementDestination === 'wallet' ? 'Wallet' : 'Bank'
            };

            const response = await axios.post(apiUrl, payload, { headers });

            const isSuccess = response.data?.status === 'SUCCESS' ||
                response.data?.status === 'success' ||
                response.data?.code === 200 ||
                response.status === 200;

            if (isSuccess) {
                toast.success(response.data?.message || 'Settlement request submitted successfully!');

                // Save locally to admin_settlement_batches so history immediately updates
                const newBatch = {
                    id: response.data?.settle_id || `SETTLE-${Date.now().toString().slice(-6)}`,
                    settle_id: response.data?.settle_id || `SETTLE-${Date.now().toString().slice(-6)}`,
                    retailer_id: String(cus_id),
                    remaining_amount: remainingAmount,
                    amount: totalRemittance?.totalAmount || remainingAmount,
                    status: "Pending",
                    settle_type: settlementDestination === 'wallet' ? 'Wallet' : 'Bank',
                    order_id: JSON.stringify(ordersArray),
                    created_at: new Date().toISOString(),
                };
                try {
                    const currentLocal = JSON.parse(localStorage.getItem("admin_settlement_batches") || "[]");
                    localStorage.setItem("admin_settlement_batches", JSON.stringify([newBatch, ...currentLocal]));
                } catch { }

                setActivePopup(null);
                await fetchCod();
            } else {
                const errorMessage = typeof response.data?.message === 'object'
                    ? Object.values(response.data.message).join(', ')
                    : response.data?.message || 'Something went wrong!';
                toast.error(errorMessage);
            }
        } catch (error) {
            console.error('API error:', error);
            toast.error(error?.response?.data?.message || 'Something went wrong!');
        } finally {
            setLoading(false);
        }
    };

    // Filter orders in modal with multi-field search (ID, AWB, Consignee, Destination)
    const filteredOrders = selectedOrders.filter(o => {
        if (!orderSearchTerm.trim()) return true;
        const q = orderSearchTerm.toLowerCase();
        return String(o.order_id || '').toLowerCase().includes(q) ||
            String(o.awb_number || '').toLowerCase().includes(q) ||
            String(o.consignee_name || '').toLowerCase().includes(q) ||
            String(o.destination || '').toLowerCase().includes(q);
    });

    return (
        <div className={`h-screen flex flex-col overflow-hidden transition-colors duration-300 ${isDarkMode ? 'bg-[#0B1120] text-gray-100' : 'bg-slate-50 text-slate-800'}`}>
            {/* Ambient Background Glows */}
            <div className="pointer-events-none fixed -top-32 -left-24 h-96 w-96 rounded-full bg-indigo-500/15 dark:bg-indigo-500/20 blur-3xl" />
            <div className="pointer-events-none fixed top-1/4 -right-24 h-96 w-96 rounded-full bg-emerald-500/10 dark:bg-emerald-500/15 blur-3xl" />
            <div className="pointer-events-none fixed -bottom-32 left-1/3 h-96 w-96 rounded-full bg-purple-500/10 dark:bg-purple-500/15 blur-3xl" />

            <NavBar />

            <div className="flex flex-1 overflow-hidden relative">
                {isToggleSidebar && (
                    <div
                        className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden transition-opacity duration-300"
                        onClick={() => setIsToggleSidebar(false)}
                    />
                )}

                <div
                    className={`
                        fixed inset-y-0 left-0 z-50 w-64
                        ${isToggleSidebar ? "translate-x-0" : "-translate-x-full"}
                        md:static md:inset-auto md:z-auto md:translate-x-0
                        ${isToggleSidebar ? "md:w-64" : "md:w-[72px]"}
                        m-3
                        rounded-3xl
                        overflow-hidden
                        transition-all
                        duration-300
                        ease-in-out
                        shadow-2xl md:shadow-none
                    `}
                >
                    <Sidebar />
                </div>

                {/* Main Scrollable Content */}
                <div className="flex-1 p-3 sm:p-4 md:p-5 overflow-y-auto custom-scrollbar">
                    <div className="max-w-7xl mx-auto space-y-3 pb-4">

                        {/* Compact Top Header Bar */}
                        <div className={`rounded-xl border px-3.5 py-2.5 transition-all duration-200 ${
                            isDarkMode
                                ? 'bg-[#0F172A]/90 border-slate-800'
                                : 'bg-white border-slate-200/90 shadow-xs'
                        }`}>
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                                <div className="flex items-center gap-2.5">
                                    <div className="p-1.5 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-xs">
                                        <FaRupeeSign className="w-3.5 h-3.5" />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h1 className="text-sm sm:text-base font-bold tracking-tight text-slate-900 dark:text-white">
                                                COD Remittance Dashboard
                                            </h1>
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25">
                                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                                LIVE
                                            </span>
                                        </div>
                                        <p className={`text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                            Real-time COD collections, automated payouts & settlement cycles
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 flex-wrap">
                                    {typeof selectedPlan === 'string' && selectedPlan && (
                                        <span className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border text-[10px] font-semibold ${
                                            isDarkMode ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                        }`}>
                                            <ShieldCheck className="w-3 h-3 text-emerald-500" />
                                            Active Plan: <span className="font-bold underline decoration-emerald-500">{String(selectedPlan)}</span>
                                        </span>
                                    )}
                                    <button
                                        onClick={() => fetchCod()}
                                        disabled={loading}
                                        className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border text-[10px] font-medium transition-colors cursor-pointer ${
                                            isDarkMode ? 'bg-white/5 hover:bg-white/10 text-slate-200 border-white/10' : 'bg-white hover:bg-slate-100 text-slate-700 border-slate-200 shadow-xs'
                                        }`}
                                        title="Refresh Remittance Data"
                                    >
                                        <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin text-indigo-400' : ''}`} />
                                        Refresh
                                    </button>
                                    <button
                                        onClick={handleExport}
                                        className="flex items-center gap-1 px-3 py-1 rounded-lg text-[10px] font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-xs transition-colors cursor-pointer"
                                    >
                                        <Download className="w-3 h-3" />
                                        Export CSV
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* 3 Metric Cards - Clean & Compact */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 sm:gap-3">
                            {remittanceData.map((card) => {
                                const isTotal = card.id === 'total';
                                const isLast = card.id === 'last';
                                const isNext = card.id === 'next';

                                return (
                                    <div
                                        key={card.id}
                                        className={`rounded-xl border transition-all duration-200 flex flex-col justify-between ${
                                            isDarkMode
                                                ? 'bg-[#0F172A] border-slate-800 hover:border-slate-700'
                                                : 'bg-white border-slate-200/90 shadow-xs hover:border-slate-300'
                                        } p-3.5`}
                                    >
                                        <div>
                                            {/* Header row: Icon + Title + Status badge */}
                                            <div className="flex items-center justify-between gap-2 mb-2">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <div className={`p-1.5 rounded-lg shrink-0 ${
                                                        isTotal
                                                            ? 'bg-indigo-500/10 text-indigo-500'
                                                            : isLast
                                                                ? 'bg-emerald-500/10 text-emerald-500'
                                                                : 'bg-amber-500/10 text-amber-500'
                                                    }`}>
                                                        {card.icon}
                                                    </div>
                                                    <h3 className={`text-[11px] font-bold uppercase tracking-wider truncate ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                        {card.title}
                                                    </h3>
                                                </div>

                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold border shrink-0 ${card.badgeBg}`}>
                                                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse"></span>
                                                    {card.badge}
                                                </span>
                                            </div>

                                            {/* Main Amount */}
                                            <div className="mb-2">
                                                <div className="text-xl sm:text-2xl font-black tracking-tight text-slate-900 dark:text-white">
                                                    {card.amount}
                                                </div>
                                                <p className={`text-[10px] mt-0.5 font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                    {card.description}
                                                </p>
                                            </div>

                                            {/* Compact stats pill box */}
                                            <div className={`rounded-lg p-2 mb-2.5 text-[10px] space-y-1 border ${
                                                isDarkMode ? 'bg-white/[0.02] border-white/5' : 'bg-slate-50 border-slate-100'
                                            }`}>
                                                {isTotal && (
                                                    <>
                                                        <div className="flex justify-between items-center">
                                                            <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Gross COD:</span>
                                                            <span className="font-semibold">₹{parseFloat(card.apiData?.totalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                                        </div>
                                                        <div className="flex justify-between items-center">
                                                            <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Fee ({card.apiData?.chargePercent}%):</span>
                                                            <span className="font-semibold text-rose-500">-₹{parseFloat(card.apiData?.chargeAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                                        </div>
                                                        <div className="flex justify-between items-center pt-1 border-t border-slate-200 dark:border-white/5 font-bold">
                                                            <span className="text-emerald-600 dark:text-emerald-400">Net Payable:</span>
                                                            <span className="text-emerald-600 dark:text-emerald-400 font-extrabold">₹{parseFloat(card.apiData?.remainingAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                                        </div>
                                                    </>
                                                )}

                                                {isLast && (
                                                    <>
                                                        <div className="flex justify-between items-center">
                                                            <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Settled On:</span>
                                                            <span className="font-semibold">
                                                                {card.apiData?.date
                                                                    ? new Date(card.apiData.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                                                                    : settedCodData?.last_remittance_date
                                                                        ? new Date(settedCodData.last_remittance_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                                                                        : '—'}
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between items-center">
                                                            <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Status:</span>
                                                            <span className="font-bold text-emerald-600 dark:text-emerald-400">
                                                                ✓ {card.apiData?.status || (card.apiData?.date || card.apiData?.orders?.length > 0 ? 'Completed' : 'No Settlement')}
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between items-center pt-1 border-t border-slate-200 dark:border-white/5 font-bold">
                                                            <span>Settled Amount:</span>
                                                            <span className="font-extrabold">₹{parseFloat(card.apiData?.remainingAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                                        </div>
                                                    </>
                                                )}

                                                {isNext && (
                                                    <>
                                                        <div className="flex justify-between items-center">
                                                            <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Scheduled:</span>
                                                            <span className="font-semibold">
                                                                {card.apiData?.date
                                                                    ? new Date(card.apiData.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                                                                    : settedCodData?.next_remittance_date
                                                                        ? new Date(settedCodData.next_remittance_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                                                                        : '—'}
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between items-center">
                                                            <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Cycle Status:</span>
                                                            <span className="font-bold text-amber-500">
                                                                ⏳ {card.apiData?.cycleStatus || (card.apiData?.date ? 'Pending Auto Payout' : 'No Schedule')}
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between items-center pt-1 border-t border-slate-200 dark:border-white/5 font-bold">
                                                            <span>Estimated Amount:</span>
                                                            <span className="font-extrabold">₹{parseFloat(card.apiData?.remainingAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* Footer action button */}
                                        <div className="flex items-center gap-1.5 pt-1">
                                            {isTotal ? (
                                                <>
                                                    <button
                                                        onClick={() => handleSettleNow('total')}
                                                        disabled={!card.buttonEnabled}
                                                        className={`flex-1 py-1.5 px-2.5 rounded-lg font-bold text-[11px] transition-all flex items-center justify-center gap-1.5 ${
                                                            card.buttonEnabled
                                                                ? 'bg-emerald-600 hover:bg-emerald-500 text-white cursor-pointer shadow-xs'
                                                                : 'bg-slate-100 dark:bg-white/5 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                                                        }`}
                                                    >
                                                        <Zap className="w-3 h-3" />
                                                        {card.buttonEnabled ? 'Settle Balance' : 'Settlement Not Ready'}
                                                    </button>
                                                    <button
                                                        onClick={() => handleOpenModal('total')}
                                                        className={`p-1.5 rounded-lg border transition-colors cursor-pointer ${
                                                            isDarkMode ? 'border-white/10 hover:bg-white/10 text-slate-300' : 'border-slate-200 hover:bg-slate-100 text-slate-700'
                                                        }`}
                                                        title="Inspect Orders"
                                                    >
                                                        <Eye className="w-3.5 h-3.5" />
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    onClick={() => handleOpenModal(isLast ? 'last' : 'next')}
                                                    className={`w-full py-1.5 px-2.5 rounded-lg font-bold text-[11px] transition-all flex items-center justify-center gap-1.5 border cursor-pointer ${
                                                        isDarkMode
                                                            ? 'border-indigo-500/30 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-300'
                                                            : 'border-indigo-200 bg-indigo-50/70 hover:bg-indigo-100/70 text-indigo-700'
                                                    }`}
                                                >
                                                    <Eye className="w-3 h-3" />
                                                    View Orders ({card.apiData?.orders?.length || 0})
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Payout Frequency Plans - Compact & Clean */}
                        <div className={`rounded-xl border p-3 sm:p-3.5 transition-all ${
                            isDarkMode ? 'bg-[#0F172A] border-slate-800' : 'bg-white border-slate-200/90 shadow-xs'
                        }`}>
                            <div className="flex items-center justify-between mb-2.5">
                                <div>
                                    <h2 className="text-xs sm:text-sm font-bold tracking-tight text-slate-900 dark:text-white">
                                        Payout Frequency Plans
                                    </h2>
                                    <p className={`text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                        Choose the payout frequency that aligns with your operational cash flow
                                    </p>
                                </div>
                                <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-md border hidden sm:inline-block ${
                                    isDarkMode ? 'bg-white/5 border-white/10 text-slate-300' : 'bg-slate-100 border-slate-200 text-slate-600'
                                }`}>
                                    Instant Switching • 0 Hidden Fees
                                </span>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                                {payoutCards.map((card) => {
                                    const isCurrentActive = String(selectedPlan || '').toLowerCase() === String(card.id).toLowerCase();

                                    return (
                                        <div
                                            key={card.id}
                                            className={`relative rounded-xl border p-2.5 transition-all flex flex-col justify-between ${
                                                isCurrentActive
                                                    ? (isDarkMode
                                                        ? 'ring-2 ring-emerald-500/80 border-emerald-500/40 bg-emerald-500/10'
                                                        : 'ring-2 ring-emerald-500 border-emerald-400 bg-emerald-50/60 shadow-xs')
                                                    : (isDarkMode
                                                        ? 'bg-white/[0.02] border-slate-800 hover:border-slate-700'
                                                        : 'bg-white border-slate-200 hover:border-slate-300')
                                            }`}
                                        >
                                            <div>
                                                <div className="flex items-center justify-between mb-1.5">
                                                    <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold text-white bg-gradient-to-r ${card.tagColor}`}>
                                                        {card.tag}
                                                    </span>
                                                    {isCurrentActive && (
                                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-extrabold bg-emerald-500 text-white">
                                                            <Check className="w-2 h-2 stroke-[3]" /> ACTIVE
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="flex items-center gap-1.5 mb-1">
                                                    <div className={`p-1 rounded-md text-white ${card.iconBg} shrink-0`}>
                                                        {React.cloneElement(card.icon, { className: "w-3 h-3" })}
                                                    </div>
                                                    <h3 className={`text-[11px] font-bold truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                                        {card.title}
                                                    </h3>
                                                </div>

                                                <div className="flex items-baseline gap-1 my-1">
                                                    <span className="text-base font-black text-indigo-600 dark:text-indigo-400">
                                                        {card.percentage}
                                                    </span>
                                                    <span className={`text-[9px] font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                        of COD (incl. GST)
                                                    </span>
                                                </div>

                                                <div className={`text-[9px] font-semibold mb-2 py-0.5 px-1.5 rounded inline-block ${
                                                    isDarkMode ? 'bg-white/5 text-slate-300' : 'bg-slate-100 text-slate-600'
                                                }`}>
                                                    {card.frequency}
                                                </div>
                                            </div>

                                            <button
                                                onClick={() => !isCurrentActive && handleActivatePayout(card.id)}
                                                disabled={isCurrentActive || loadingCardId === card.id}
                                                className={`w-full py-1 px-2 rounded-lg font-bold text-[10px] transition-all flex items-center justify-center gap-1 cursor-pointer ${
                                                    isCurrentActive
                                                        ? (isDarkMode
                                                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 cursor-default'
                                                            : 'bg-emerald-100 text-emerald-700 border border-emerald-300 cursor-default')
                                                        : (isDarkMode
                                                            ? 'bg-white text-slate-900 hover:bg-slate-200'
                                                            : 'bg-red-600 hover:bg-red-700 text-white shadow-xs')
                                                }`}
                                            >
                                                {loadingCardId === card.id ? (
                                                    <div className="py-0.5"><Spinner /></div>
                                                ) : isCurrentActive ? (
                                                    <>
                                                        <Check className="w-2.5 h-2.5 stroke-[3]" />
                                                        Active Plan
                                                    </>
                                                ) : (
                                                    "Activate"
                                                )}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Quick Actions Hub - Compact */}
                        <div className={`rounded-xl border p-3 transition-all ${
                            isDarkMode ? 'bg-[#0F172A] border-slate-800' : 'bg-white border-slate-200/90 shadow-xs'
                        }`}>
                            <div className="mb-2">
                                <h2 className="text-xs sm:text-sm font-bold tracking-tight text-slate-900 dark:text-white">
                                    Quick Actions
                                </h2>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                <div
                                    onClick={() => handleSettleNow('total')}
                                    className={`p-2.5 rounded-lg border transition-all cursor-pointer flex items-center justify-between gap-2 ${
                                        isDarkMode ? 'bg-white/[0.02] border-slate-800 hover:bg-white/[0.05]' : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                                    }`}
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        <div className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-500 shrink-0">
                                            <Zap className="w-3.5 h-3.5" />
                                        </div>
                                        <div className="min-w-0">
                                            <h3 className="text-xs font-bold truncate">Request Settlement</h3>
                                            <p className={`text-[10px] truncate ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Instant transfer to Wallet/Bank</p>
                                        </div>
                                    </div>
                                    <ArrowUpRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                </div>

                                <div
                                    onClick={() => handleOpenModal('total')}
                                    className={`p-2.5 rounded-lg border transition-all cursor-pointer flex items-center justify-between gap-2 ${
                                        isDarkMode ? 'bg-white/[0.02] border-slate-800 hover:bg-white/[0.05]' : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                                    }`}
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        <div className="p-1.5 rounded-lg bg-indigo-500/10 text-indigo-500 shrink-0">
                                            <TrendingUp className="w-3.5 h-3.5" />
                                        </div>
                                        <div className="min-w-0">
                                            <h3 className="text-xs font-bold truncate">View Analytics</h3>
                                            <p className={`text-[10px] truncate ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Order logs & breakdown</p>
                                        </div>
                                    </div>
                                    <ArrowUpRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                </div>

                                <div
                                    onClick={() => navigate('/profile')}
                                    className={`p-2.5 rounded-lg border transition-all cursor-pointer flex items-center justify-between gap-2 ${
                                        isDarkMode ? 'bg-white/[0.02] border-slate-800 hover:bg-white/[0.05]' : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                                    }`}
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        <div className="p-1.5 rounded-lg bg-purple-500/10 text-purple-500 shrink-0">
                                            <CreditCard className="w-3.5 h-3.5" />
                                        </div>
                                        <div className="min-w-0">
                                            <h3 className="text-xs font-bold truncate">Payment Methods</h3>
                                            <p className={`text-[10px] truncate ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Manage bank accounts</p>
                                        </div>
                                    </div>
                                    <ArrowUpRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            {/* Request Settlement Modal */}
            {activePopup && (
                <div className="fixed inset-0 bg-black/75 backdrop-blur-md flex items-center justify-center z-50 p-3 sm:p-4 animate-in fade-in duration-200">
                    <div className={`relative max-w-md w-full rounded-2xl border shadow-2xl overflow-hidden transition-all duration-300 ${
                        isDarkMode ? 'bg-[#0F172A] text-white border-slate-700' : 'bg-white text-slate-900 border-slate-200'
                    }`}>
                        {/* Top decorative gradient bar */}
                        <div className="h-1.5 w-full bg-gradient-to-r from-indigo-500 via-purple-500 to-emerald-500" />

                        <button
                            onClick={closePopup}
                            className={`absolute top-3.5 right-3.5 p-1.5 rounded-full transition-colors cursor-pointer ${
                                isDarkMode ? 'hover:bg-white/10 text-slate-400 hover:text-white' : 'hover:bg-slate-100 text-slate-500 hover:text-black'
                            }`}
                        >
                            <X className="w-4 h-4" />
                        </button>

                        <div className="p-5">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2.5 rounded-2xl bg-gradient-to-tr from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/30">
                                    <Zap className="w-5 h-5" />
                                </div>
                                <div>
                                    <h2 className="text-base font-bold tracking-tight">
                                        Request Remittance Settlement
                                    </h2>
                                    <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                        Instant transfer of available funds
                                    </p>
                                </div>
                            </div>

                            {/* Summary Table */}
                            <div className={`rounded-xl border p-3.5 mb-4 ${
                                isDarkMode ? 'bg-white/[0.03] border-white/10' : 'bg-slate-50 border-slate-200'
                            }`}>
                                <div className="space-y-2 text-xs">
                                    <div className="flex justify-between items-center">
                                        <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Total COD Amount</span>
                                        <span className="font-semibold">
                                            ₹{remittanceData.find(c => c.id === activePopup)?.apiData?.totalAmount || '0.00'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Processing Fee ({remittanceData.find(c => c.id === activePopup)?.apiData?.chargePercent || '0'}%)</span>
                                        <span className="font-semibold text-rose-500">
                                            -₹{remittanceData.find(c => c.id === activePopup)?.apiData?.chargeAmount || '0.00'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Orders Included</span>
                                        <span className="font-semibold">
                                            {remittanceData.find(c => c.id === activePopup)?.apiData?.orders?.length || 0} Orders
                                        </span>
                                    </div>
                                    <div className={`pt-2 border-t flex justify-between items-center ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
                                        <span className="font-bold text-emerald-600 dark:text-emerald-400">Total Settlement Payout</span>
                                        <span className="font-black text-base text-emerald-600 dark:text-emerald-400">
                                            ₹{remittanceData.find(c => c.id === activePopup)?.apiData?.remainingAmount || '0.00'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Destination Selector */}
                            <div className="mb-4">
                                <label className={`block text-xs font-bold mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                    Select Settlement Destination:
                                </label>
                                <div className="grid grid-cols-2 gap-2.5">
                                    {/* Wallet Option */}
                                    <div
                                        onClick={() => setSettlementDestination('wallet')}
                                        className={`p-3 rounded-xl border-2 transition-all cursor-pointer flex items-center gap-2.5 ${
                                            settlementDestination === 'wallet'
                                                ? (isDarkMode
                                                    ? 'border-indigo-500 bg-indigo-500/15 shadow-sm'
                                                    : 'border-indigo-600 bg-indigo-50/70 shadow-sm')
                                                : (isDarkMode ? 'border-slate-800 hover:border-slate-700' : 'border-slate-200 hover:border-slate-300')
                                        }`}
                                    >
                                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                            settlementDestination === 'wallet'
                                                ? 'border-indigo-600 bg-indigo-600'
                                                : 'border-slate-400'
                                        }`}>
                                            {settlementDestination === 'wallet' && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-1.5 font-bold text-xs">
                                                <Wallet className="w-3.5 h-3.5 text-indigo-500" />
                                                Wallet
                                            </div>
                                            <span className={`text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                Instant Credit
                                            </span>
                                        </div>
                                    </div>

                                    {/* Bank Account Option */}
                                    <div
                                        onClick={() => setSettlementDestination('bank')}
                                        className={`p-3 rounded-xl border-2 transition-all cursor-pointer flex items-center gap-2.5 ${
                                            settlementDestination === 'bank'
                                                ? (isDarkMode
                                                    ? 'border-indigo-500 bg-indigo-500/15 shadow-sm'
                                                    : 'border-indigo-600 bg-indigo-50/70 shadow-sm')
                                                : (isDarkMode ? 'border-slate-800 hover:border-slate-700' : 'border-slate-200 hover:border-slate-300')
                                        }`}
                                    >
                                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                            settlementDestination === 'bank'
                                                ? 'border-indigo-600 bg-indigo-600'
                                                : 'border-slate-400'
                                        }`}>
                                            {settlementDestination === 'bank' && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-1.5 font-bold text-xs">
                                                <Building2 className="w-3.5 h-3.5 text-indigo-500" />
                                                Bank Account
                                            </div>
                                            <span className={`text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                Direct NEFT/RTGS
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* View Orders button */}
                            <button
                                onClick={() => {
                                    const activeCard = remittanceData.find(card => card.id === activePopup);
                                    setSelectedOrders(activeCard?.apiData?.orders || []);
                                    setIsModalOpen(true);
                                }}
                                className={`w-full py-2 px-3 rounded-xl text-xs font-semibold mb-4 border transition-colors flex items-center justify-center gap-1.5 cursor-pointer ${
                                    isDarkMode
                                        ? 'border-white/10 bg-white/5 hover:bg-white/10 text-slate-300'
                                        : 'border-slate-200 bg-slate-100 hover:bg-slate-200 text-slate-700'
                                }`}
                            >
                                <Eye className="w-3.5 h-3.5" />
                                Inspect {remittanceData.find(card => card.id === activePopup)?.apiData?.orders?.length || 0} Orders
                            </button>

                            {/* Action Buttons */}
                            <div className="flex gap-2.5">
                                <button
                                    onClick={closePopup}
                                    className={`flex-1 py-2.5 px-3 rounded-xl font-bold text-xs border transition-colors cursor-pointer ${
                                        isDarkMode
                                            ? 'border-slate-700 text-slate-300 hover:bg-white/5'
                                            : 'border-slate-200 text-slate-700 hover:bg-slate-100'
                                    }`}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={reqForSettelod}
                                    disabled={loading}
                                    className="flex-1 py-2.5 px-3 rounded-xl font-bold text-xs text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-md shadow-emerald-500/25 transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                                >
                                    {loading ? <Spinner /> : (
                                        <>
                                            <Zap className="w-3.5 h-3.5" />
                                            Confirm & Request
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Order Details Modal with Search */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/75 backdrop-blur-md z-50 flex items-center justify-center p-3 sm:p-4 animate-in fade-in duration-200">
                    <div className={`rounded-2xl border shadow-2xl w-full max-w-lg overflow-hidden transition-all duration-300 ${
                        isDarkMode ? 'bg-[#0F172A] text-white border-slate-700' : 'bg-white text-slate-900 border-slate-200'
                    }`}>
                        {/* Header */}
                        <div className={`p-4 border-b flex items-center justify-between ${
                            isDarkMode ? 'border-slate-800 bg-slate-900/50' : 'border-slate-200 bg-slate-50'
                        }`}>
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 rounded-lg bg-indigo-500/10 text-indigo-500 border border-indigo-500/20">
                                    <Receipt className="w-4 h-4" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-bold">Included Remittance Orders</h3>
                                    <p className={`text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                        Showing {filteredOrders.length} of {selectedOrders.length} orders
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className={`p-1.5 rounded-full transition-colors cursor-pointer ${
                                    isDarkMode ? 'hover:bg-white/10 text-slate-400 hover:text-white' : 'hover:bg-slate-200 text-slate-500 hover:text-black'
                                }`}
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Search Bar */}
                        <div className="p-3 border-b border-slate-200 dark:border-slate-800">
                            <div className="relative">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                    type="text"
                                    value={orderSearchTerm}
                                    onChange={(e) => setOrderSearchTerm(e.target.value)}
                                    placeholder="Search by Order ID, AWB, Consignee, Destination..."
                                    className={`w-full pl-9 pr-3 py-1.5 rounded-xl text-xs border transition-colors outline-hidden ${
                                        isDarkMode
                                            ? 'bg-slate-800/80 border-slate-700 text-white placeholder-slate-500 focus:border-indigo-500'
                                            : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-indigo-500'
                                    }`}
                                />
                            </div>
                        </div>

                        {/* Orders List */}
                        <div className="p-3 max-h-80 overflow-y-auto custom-scrollbar">
                            {filteredOrders.length > 0 ? (
                                <div className="space-y-2">
                                    {filteredOrders.map((order, idx) => (
                                        <div
                                            key={idx}
                                            className={`p-2.5 rounded-xl border flex items-center justify-between text-xs transition-colors ${
                                                isDarkMode
                                                    ? 'bg-white/[0.02] border-slate-800 hover:bg-white/[0.05]'
                                                    : 'bg-slate-50/70 border-slate-200 hover:bg-slate-100'
                                            }`}
                                        >
                                            <div className="min-w-0 pr-2">
                                                <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                                                    <span className={`text-[9px] font-mono px-1.5 py-0.2 rounded-md ${
                                                        isDarkMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-200 text-slate-700'
                                                    }`}>
                                                        #{idx + 1}
                                                    </span>
                                                    <span className="font-mono font-bold text-indigo-500 dark:text-indigo-400">
                                                        {order.order_id}
                                                    </span>
                                                    {order.awb_number && order.awb_number !== "—" && (
                                                        <span className={`text-[9px] font-mono px-1 py-0.2 rounded border ${
                                                            isDarkMode ? 'border-slate-700 text-slate-400' : 'border-slate-300 text-slate-500'
                                                        }`}>
                                                            AWB: {order.awb_number}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1.5 text-[10px] text-slate-500 dark:text-slate-400 truncate">
                                                    {order.consignee_name && order.consignee_name !== "—" && (
                                                        <span className="font-medium truncate">{order.consignee_name}</span>
                                                    )}
                                                    {order.destination && order.destination !== "—" && (
                                                        <span>• {order.destination}</span>
                                                    )}
                                                    {order.date && order.date !== "—" && (
                                                        <span>• {order.date}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <span className="font-bold text-emerald-600 dark:text-emerald-400 text-sm">
                                                    ₹{parseFloat(order.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                </span>
                                                {order.feeAmount && parseFloat(order.feeAmount) > 0 ? (
                                                    <span className="text-[9px] text-rose-500 block">
                                                        -₹{parseFloat(order.feeAmount).toFixed(2)} Fee
                                                    </span>
                                                ) : (
                                                    <span className="text-[9px] text-emerald-500 block">0% Fee</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <Receipt className="w-8 h-8 text-slate-400 mx-auto mb-2 opacity-50" />
                                    <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                        {selectedOrders.length === 0 ? 'No orders recorded in this remittance cycle.' : 'No orders match your search filter.'}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Footer Total */}
                        <div className={`p-3 border-t flex items-center justify-between ${
                            isDarkMode ? 'border-slate-800 bg-slate-900/50' : 'border-slate-200 bg-slate-50'
                        }`}>
                            <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                Total Orders: {filteredOrders.length}
                            </span>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className={`px-4 py-1.5 rounded-xl text-xs font-bold transition-colors cursor-pointer ${
                                    isDarkMode
                                        ? 'bg-white text-slate-900 hover:bg-slate-200'
                                        : 'bg-slate-900 text-white hover:bg-slate-800'
                                }`}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PageRemittances;